/**
 * Rozet ve Sertifikalar — the reference's `data-screen="credentials"`.
 *
 * Ported against the rendered DOM of `Screens.credentials` / `renderCredTable`
 * (`m08b-admin2.js:7`): the static page head, the four-control toolbar, and one row per record in
 * the register, newest first. The register is the record; the artwork is only its presentation,
 * which is why the status column shows a DERIVED state — a revoked record with a successor reads
 * «Yerine yenisi düzenlendi» and an active one past its validity reads «Süresi dolmuş», neither of
 * which is stored (`credentialViewState`).
 *
 * Three dialogs hang off the screen, as upstream: «+ Tek Credential» issues one record from a
 * published release, the paper-plane re-sends the e-mail, and the × revokes. The resend dialog is
 * the one that carries a warning worth keeping: on a record still awaiting acceptance a resend
 * MINTS A NEW TOKEN and kills the link already in the recipient's inbox, and the dialog says so
 * before the button is pressed.
 */
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { newIdempotencyKey } from '../../api/client';
import type { FirstSendScopeBody, CredentialGroupView } from '../../api/types';
import { useAdoptCredentials } from '../data/adopt';
import { useV07Commands } from '../data/commands-context';
import type { BatchLookup } from '../data/queries';
import { useV07Queries } from '../data/queries-context';
import { useV07Account, useV07Role } from '../data/session';
import { useDemoData } from '../data/store';
import type { DemoCredential, DemoFieldDefinition, DemoRelease, DemoTemplate } from '../data/types';
import { type ReadFailure, useRead } from '../data/use-read';
import { RESEND_TARGET_TEXT, useResendTarget } from '../data/use-resend-target';
import { downloadCsv } from '../lib/credential-export';
import {
  claimTokenView,
  claimUrlStatus,
  hasActionReason,
  type IssueProblem,
  mintPublicCode,
  normalizeIssueClaims,
  validateIssue,
} from '../lib/credential-register';
import { engagementFallback, PROFILE_ADD_BADGE, SHARE_BADGE } from '../lib/engagement-labels';
import {
  acknowledgeUnusableIntent,
  clearPendingIntent,
  readPendingIntent,
  writePendingIntent,
} from '../lib/pending-intent';
import {
  credentialViewState,
  credTitle,
  currentRelease,
  dataTypeRec,
  deliveryLabel,
  displayName,
  editableFields,
  fieldLabel,
  fmtDate,
  fmtDateTime,
  fmtNum,
  nextExpiry,
  publishedTemplates,
  pubPolicyRec,
  STATUS_ICON,
  STATUS_PILL,
  STATUS_TR,
  stateForPolicy,
  todayIso,
  VISIBILITY_TR,
} from '../lib/reference';
import { typeRec } from '../lib/render';
import { SEND_ACTS, SEND_REQUEST_KINDS, type SendRequestKind } from '../lib/bulk-send';
import {
  TEMPLATE_CELL_LINE_STYLE,
  TEMPLATE_CELL_STYLE,
  templateCellFull,
  templateCellLines,
  templateCellNameIsClipped,
} from '../lib/template-cell-name';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';
import { BulkClaimLinkPanel } from './BulkClaimLinkPanel';
import { FirstSendPanel } from './FirstSendPanel';

/** The prototype signs every administrative command with the demo operator. */
const ACTOR = 'Türker Baş';
const PER_PAGE = 25;

/**
 * `LIST` — the reference keeps its filter state in a module-level object (`m08b-admin2.js:6`), so
 * leaving the register and coming back keeps the search box and the two selects as they were.
 * Only a query parameter overwrites them, and only the parameters the URL actually carries.
 */
interface RegisterState {
  q: string;
  status: string;
  template: string;
  page: number;
}

const LIST: RegisterState = { q: '', status: '', template: '', page: 1 };

function IconEye(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconSend(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function IconX(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

const deliveryPill = deliveryLabel;

const typeLabel = (r: DemoRelease): string =>
  r.credentialType === 'custom' && r.customTypeLabel ? r.customTypeLabel : typeRec(r.credentialType).tr;

// ------------------------------------------------------------------------------------- dialogs

function RevokeDialog({
  credential,
  onClose,
}: {
  credential: DemoCredential;
  onClose: (reason: string | null) => void;
}): ReactElement {
  const [reason, setReason] = useState('');
  return (
    <Modal label="İptal" onClose={() => onClose(null)}>
      <ModalHead title="Kayıt iptal edilsin mi?" onClose={() => onClose(null)} />
      <div className="card-pad">
        <p>
          <b>{displayName(credential)}</b>
          {` · ${credTitle(credential, 'tr')} · `}
          <span className="mono">{credential.publicCode}</span>
        </p>
        <div className="field">
          <label htmlFor="rvReason">Neden (özel; kayıt defterine yazılır, public sayfada gösterilmez)</label>
          <textarea
            className="textarea"
            id="rvReason"
            placeholder="ör. Yanlış alıcıya düzenlendi"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="info-box">
          {'Public sayfa silinmez; aynı adreste '}
          <b>«İptal edildi»</b>
          {
            ' durumu ve iptal tarihi gösterilir, görsele filigran basılır. İptal geri alınamaz — yanlış alıcı için yeni kayıt (reissue) çıkarılır, eski kaydın geçmişi korunur.'
          }
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={() => onClose(null)}>
          Vazgeç
        </button>
        <button className="btn btn-dark" id="rvGo" type="button" onClick={() => onClose(reason)}>
          İptal et
        </button>
      </div>
    </Modal>
  );
}

function ResendDialog({
  credential,
  claimUrl,
  onClose,
}: {
  credential: DemoCredential;
  claimUrl: { available: boolean; message: string };
  onClose: (reason: string | null) => void;
}): ReactElement {
  const pending = credential.state === 'pending_acceptance';
  const tok = claimTokenView(credential);
  const [reason, setReason] = useState(
    pending ? 'Alıcı onay bağlantısına ulaşamadı' : 'Alıcı e-postayı bulamadı',
  );
  const [error, setError] = useState(false);
  const target = useResendTarget(credential);
  /* One press is one send. A deliberate LATER send is a new window; a second click is not. */
  const submitted = useRef(false);

  const go = (): void => {
    if (submitted.current || target.status !== 'ready') return;
    if (!hasActionReason(reason)) {
      setError(true);
      return;
    }
    submitted.current = true;
    onClose(reason);
  };

  return (
    <Modal label="Yeniden gönder" onClose={() => onClose(null)} wide>
      <ModalHead
        title={
          pending ? 'Yeni onay bağlantısı üret ve yeniden gönder' : 'Bilgilendirme e-postasını yeniden gönder'
        }
        onClose={() => onClose(null)}
      />
      <div className="card-pad stack" style={{ gap: 12 }}>
        {pending ? (
          <div className="warn-box">
            <b>Bu işlem YENİ bir onay bağlantısı üretir.</b>
            {` Alıcıya daha önce gönderilmiş bağlantı geçersiz olur; yalnız yeni bağlantı çalışır.${
              claimUrl.available
                ? ''
                : ' Mevcut ham bağlantı bu oturumda bulunmadığı için zaten yalnız yeni bağlantı üretilebilir.'
            }`}
          </div>
        ) : (
          <div className="info-box">
            Bu kayıt yayınlanmış. Alıcıya sertifikanın herkese açık sayfasını içeren bir bilgilendirme
            e-postası gönderilir; yeniden onay istenmez ve mevcut bağlantılar etkilenmez.
          </div>
        )}
        <dl className="dl">
          <dt>Kayıt</dt>
          <dd>
            {`${displayName(credential)} · `}
            <span className="mono">{credential.publicCode}</span>
          </dd>
          <dt>Hedef e-posta</dt>
          <dd id="rsTarget" data-target={target.status}>
            {target.status === 'ready' ? (
              <b>{target.email}</b>
            ) : (
              <span className="muted">{RESEND_TARGET_TEXT[target.status]}</span>
            )}
          </dd>
          <dt>İşlemi yapan</dt>
          <dd>{ACTOR}</dd>
          {pending && tok?.hash ? (
            <>
              <dt>Mevcut token</dt>
              <dd className="small mono">{`${tok.hash.slice(0, 12)}… · rotasyon ${tok.rotationCount}`}</dd>
            </>
          ) : null}
        </dl>
        <div className="field">
          <label htmlFor="rsReason">Gerekçe (zorunlu)</label>
          <input
            className="input"
            id="rsReason"
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(false);
            }}
          />
        </div>
        <div className="warn-box" hidden={!error}>
          Gerekçe zorunludur ve en az 3 karakter olmalıdır.
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={() => onClose(null)}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="rsGo"
          type="button"
          /* Never offered before the address is known: a send with no target is a send to nowhere. */
          disabled={target.status !== 'ready'}
          onClick={go}
        >
          {pending ? 'Yeni bağlantı üret ve gönder' : 'Yeniden gönder'}
        </button>
      </div>
    </Modal>
  );
}

interface IssueResult {
  templateId: string;
  /**
   * The release the attempt was composed and validated against.
   *
   * A retry has to repeat the FIRST attempt, not compose a fresh one. Re-reading «the current
   * release» meant that if a colleague published a new version while the first answer was missing,
   * the retry sent different content under the same idempotency key — which is either a surprise
   * for the operator or a silent no-op, depending on what the server had already stored.
   */
  releaseId: string;
  claims: Record<string, string>;
  email: string;
  issueDate: string;
  expiresAt: string;
  lang: 'tr' | 'en';
  sendEmail: boolean;
  /** The key this intent carries, for as many attempts as it takes. */
  idempotencyKey: string;
  /**
   * When the intent was FIRST sent.
   *
   * It travels with the intent because the stamp decides when the record stops being offered, and
   * restamping it on every retry meant the twelve hours were measured from the last attempt rather
   * than from the request whose outcome is unknown — so a form somebody kept retrying could hold a
   * name and an address indefinitely.
   */
  startedAt: string;
}

function IssueDialog({
  templates,
  resume,
  onSubmit,
  onCancel,
  onAbandon,
  persisted,
}: {
  templates: DemoTemplate[];
  /** An intent whose outcome nobody knows, to be carried on rather than started again. */
  resume: IssueResult | null;
  /** Performs the creation and answers what happened; the dialog stays open until it succeeded. */
  onSubmit: (result: IssueResult) => Promise<{
    ok: boolean;
    message?: string | undefined;
    code?: string | undefined;
    outcome?: 'refused' | 'unresolved' | undefined;
  }>;
  onCancel: () => void;
  /** The one way an unresolved intent is dropped: the operator says so, knowing what it means. */
  onAbandon: () => void;
  /** Whether the intent survived into this tab's storage, so the promise made matches the fact. */
  persisted: boolean;
}): ReactElement {
  const [templateId, setTemplateId] = useState(resume?.templateId ?? templates[0]?.id ?? '');
  const [claims, setClaims] = useState<Record<string, string>>(resume?.claims ?? {});
  const [email, setEmail] = useState(resume?.email ?? '');
  const [issueDate, setIssueDate] = useState(resume?.issueDate ?? todayIso());
  const [expiresAt, setExpiresAt] = useState(resume?.expiresAt ?? '');
  const [lang, setLang] = useState<'tr' | 'en'>(resume?.lang ?? 'tr');
  const [sendEmail, setSendEmail] = useState(resume?.sendEmail ?? true);
  const [problems, setProblems] = useState<IssueProblem[]>([]);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  /*
   * ONE idempotency key per intent — minted when the dialog opens, kept across every attempt.
   *
   * The command used to mint a fresh key per call, which defeats the guarantee it was asking for:
   * two calls for one intent are two keys, and the backend, doing exactly what it is told, made two
   * credentials. A double click made two documents for one person; so did pressing «Oluştur» again
   * after a request whose answer never came back — where the FIRST attempt had already created the
   * record. The key belongs to the intent, and the intent is this dialog.
   */
  const [idempotencyKey] = useState(() => resume?.idempotencyKey ?? newIdempotencyKey());
  /* True while an earlier attempt's outcome is unknown: the form is that attempt, not a new one. */
  const unresolved = resume !== null;

  const template = templates.find((t) => t.id === templateId);
  /*
   * A resumed intent keeps the exact release it was SENT with — and only that one.
   *
   * The fallback that used to sit here (`?? currentRelease(template)`) meant that if the named
   * release could not be found locally, a different release was quietly substituted under the same
   * idempotency key: the retry would then be a different request wearing the first one's name.
   * There is no release that is «close enough» to the one the first attempt used, so when it is
   * missing the form says so and sends nothing.
   */
  const release =
    resume !== null
      ? (template?.releases.find((r) => r.id === resume.releaseId) ?? null)
      : currentRelease(template);
  const fields: DemoFieldDefinition[] = release ? editableFields(release.fieldDefinitions) : [];

  /* `syncType` — the expiry follows the chosen release's own validity policy, not a free date. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: a resumed intent keeps the date it was sent with
  useEffect(() => {
    if (release === null || unresolved) return;
    setExpiresAt(nextExpiry(release.validityPolicy, issueDate) ?? '');
  }, [release, issueDate]);

  /*
   * The language the form STARTS in is the one the release says it is written in.
   *
   * `useState('tr')` used to decide this, and `issueBody` sends whatever the selector holds — so a
   * release whose Default Language is English was issued as Turkish unless the operator noticed the
   * dropdown, and the server's «no language named → the release's own» rule was never reached from
   * here. What that produced is a record whose single document is the English design filed under the
   * Turkish locale key: the Turkish page then serves the English certificate and the English page
   * has nothing to serve.
   *
   * Keyed on the chosen template and its release's ID — identities, not the release object, which is
   * rebuilt on every render — and that is the whole of the rule: it runs when the form opens and when
   * a different release is chosen, and it does not run when the operator changes anything else, so a
   * language they picked themselves is not overwritten on the next keystroke. The template is in the
   * key beside the release so this does not rest on release ids differing between two templates. A
   * resumed intent is excluded exactly as the expiry above is: it must repeat the first attempt, so
   * the language travels with the intent and no default may touch it.
   *
   * The value is read from the release's own `defaultLanguage`, normalised the way the API boundary
   * already normalises it — so a release published before the declaration existed still lands on
   * `tr`, which is the language its records have always been issued in. `tr` and `en` are not
   * ranked here; whatever the release declares is what the form offers first.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: the chosen template and its release are what change the default; a resumed intent keeps the language it was sent with
  useEffect(() => {
    if (release === null || unresolved) return;
    setLang(release.defaultLanguage === 'en' ? 'en' : 'tr');
  }, [templateId, release?.id]);

  const go = (): void => {
    /* A second click while the first is in flight is the same intent, not a second one. */
    if (busy) return;
    setRefusal(null);

    /*
     * A retry is the request that was sent. Not a request built from the form again.
     *
     * The dialog used to compose the payload from its own state every time, so a resumed intent
     * carried whatever was in the inputs at that moment. Anyone who corrected a typo in the address
     * before pressing «Aynı isteği yeniden dene» sent a DIFFERENT body under the FIRST request's
     * key — which the backend rightly refuses, and which is a promise the screen had no business
     * making. So a resumed intent is submitted verbatim, and the fields it is made of are not
     * editable while it stands: a different request is a different intent, and the way to start one
     * is «Bu isteği bırak».
     */
    if (resume !== null) {
      if (release === null) {
        setRefusal(
          'Bu isteğin yapıldığı release bu tarayıcıda bulunamadı. Aynı isteği başka bir release ile göndermek mümkün değildir; sayfayı yenileyin ya da kaydın oluşup oluşmadığını kayıt defterinden kontrol edin.',
        );
        return;
      }
      setProblems([]);
      setBusy(true);
      void onSubmit(resume).then(afterAttempt);
      return;
    }

    if (release === null) return;
    const found = validateIssue(release, claims, email, lang);
    setProblems(found);
    if (found.length > 0) return;
    setBusy(true);
    void onSubmit({
      templateId,
      releaseId: release.id,
      /* What was VALIDATED, not what was typed: «Evet» is stored as the `true` the schema allows. */
      claims: normalizeIssueClaims(release, claims),
      email,
      issueDate,
      expiresAt,
      lang,
      sendEmail,
      idempotencyKey,
      startedAt: new Date().toISOString(),
    }).then(afterAttempt);
  };

  function afterAttempt(r: {
    ok: boolean;
    message?: string | undefined;
    code?: string | undefined;
    outcome?: 'refused' | 'unresolved' | undefined;
  }): void {
    /*
     * Kept open on a refusal, with what the operator typed still in it: the dialog used to close
     * before the command even ran, so a refused creation threw the whole form away and the only
     * way to try again was to retype it — which is also the one moment a NEW key would be minted
     * for an intent the server may already have carried out.
     */
    if (!r.ok) {
      setBusy(false);
      /*
       * Two refusals that are evidence of an earlier attempt, not evidence against one. Saying
       * «Oluşturulamadı» about either would be the opposite of what the server just reported.
       */
      if (r.code === 'idempotency_in_flight') {
        setRefusal('Bu isteğin bir denemesi hâlâ sürüyor. Sonucu bekleyin; ikinci bir kayıt oluşmaz.');
        return;
      }
      if (r.code === 'idempotency_key_reused_with_different_request') {
        setRefusal(
          'Bu istek sunucuda zaten tamamlanmış ve gönderilen gövde ilkiyle aynı değil; kayıt oluşmuştur. İkinci bir kayıt üretilmedi — kayıt defterinden kontrol edin.',
        );
        return;
      }
      setRefusal(
        r.outcome === 'unresolved'
          ? /* NOT «oluşturulamadı»: nobody knows whether it was. */
            `${r.message ?? 'Sunucuya ulaşılamadı'} — bu isteğin sonucu bilinmiyor; kayıt oluşmuş olabilir. Aynı istek yeniden denendiğinde ikinci bir kayıt oluşmaz.`
          : (r.message ?? 'Kayıt oluşturulamadı'),
      );
    }
  }

  const policy = release ? pubPolicyRec(release.publicationPolicy) : null;

  return (
    <Modal label="Tek credential" onClose={onCancel}>
      <ModalHead title="Tek credential oluştur" onClose={onCancel} />
      <div className="card-pad">
        <div className="field">
          <label htmlFor="siTpl">{"Şablon (yalnız yayınlanmış release'ler)"}</label>
          <select
            className="select"
            id="siTpl"
            value={templateId}
            disabled={unresolved}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setClaims({});
              setProblems([]);
            }}
          >
            {templates.map((t) => {
              const r = currentRelease(t);
              return (
                <option key={t.id} value={t.id}>
                  {r ? `${t.name} · v${r.releaseNumber} · ${typeLabel(r)}` : t.name}
                </option>
              );
            })}
          </select>
        </div>
        <div id="siPolicy">
          {policy === null ? null : (
            <div className="info-box small">
              <b>Yayın politikası:</b>
              {` ${policy.tr} — ${policy.help}`}
            </div>
          )}
        </div>
        <div id="siFields">
          {fields.length === 0 ? (
            <p className="muted small">{"Bu release'te dinamik alan yok."}</p>
          ) : (
            fields.map((f) => (
              <div className="field" key={f.key}>
                <label htmlFor={`si_${f.key}`}>
                  {`${fieldLabel(f, lang)}${f.required ? ' *' : ''} `}
                  <span className="small muted">
                    {`${dataTypeRec(f.dataType).tr} · ${VISIBILITY_TR[f.visibility] ?? ''}`}
                  </span>
                </label>
                <input
                  className="input"
                  id={`si_${f.key}`}
                  data-si={f.key}
                  value={claims[f.key] ?? ''}
                  placeholder={f.sample}
                  disabled={unresolved}
                  onChange={(e) => setClaims((c) => ({ ...c, [f.key]: e.target.value }))}
                />
              </div>
            ))
          )}
        </div>
        <div className="field">
          <label htmlFor="siEmail">İletişim e-postası (özel)</label>
          <input
            className="input"
            id="siEmail"
            type="email"
            placeholder="ad@sirket.com"
            value={email}
            disabled={unresolved}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="siIssue">Veriliş tarihi</label>
            <input
              className="input"
              id="siIssue"
              type="date"
              value={issueDate}
              disabled={unresolved}
              onChange={(e) => setIssueDate(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="siExp">Geçerlilik</label>
            <input
              className="input"
              id="siExp"
              type="date"
              value={expiresAt}
              disabled={unresolved}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="siLang">Dil</label>
            <select
              className="select"
              id="siLang"
              value={lang}
              disabled={unresolved}
              onChange={(e) => setLang(e.target.value === 'en' ? 'en' : 'tr')}
            >
              <option value="tr">Türkçe</option>
              <option value="en">English</option>
            </select>
          </div>
          <div className="field">
            <label className="check" style={{ marginTop: 26 }}>
              <input
                type="checkbox"
                id="siSend"
                checked={sendEmail}
                disabled={unresolved}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              E-posta gönder (simülasyon)
            </label>
          </div>
        </div>
        <div className="help">
          {
            "Alanlar ve doğrulama kuralları seçilen release'in alan tanımlarından üretilir — tek kayıt, CSV ve XLSX aynı doğrulama motorunu kullanır."
          }
        </div>
        <div id="siResult">
          {problems.length === 0 ? null : (
            <div className="warn-box">
              <b>Oluşturulamadı.</b>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {problems.map((p) => (
                  <li key={`${p.key ?? ''}-${p.message}`}>{p.message}</li>
                ))}
              </ul>
            </div>
          )}
          {/* The SERVER's refusal, which is a different fact from the form's own findings. */}
          {refusal === null ? null : (
            <div className="warn-box" id="siRefusal" role="alert">
              <b>{unresolved ? 'Sonuç bilinmiyor.' : 'Oluşturulamadı.'}</b>
              {` ${refusal}`}
            </div>
          )}
          {/*
           * A resumed intent, stated on reopening — the window was closed while its outcome was
           * unknown, and this is the same request, carrying the same key.
           */}
          {unresolved && refusal === null ? (
            <div className="info-box" id="siResume">
              {
                'Sonucu bilinmeyen bir üretim isteği duruyor. Bu pencere o isteğin kendisidir: alanlar gönderildiği hâliyle, değiştirilemez olarak gösterilir ve «Aynı isteği yeniden dene» tam olarak o gövdeyi tekrar gönderir; ikinci bir kayıt oluşmaz. Başka bir içerik göndermek isterseniz bu ayrı bir istektir — önce «Bu isteği bırak».'
              }
              {/* Only the promise this tab can actually keep. A storage that refused the write still
                  retries correctly here and now; what it cannot do is survive a refresh. */}
              {persisted ? null : (
                <div className="small" id="siVolatile">
                  {
                    'Bu tarayıcı isteği saklayamadı: sekmeyi yenilerseniz bu pencere geri gelmez. Kaydın oluşup oluşmadığını kayıt defterinden kontrol edin.'
                  }
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onCancel}>
          Vazgeç
        </button>
        {/* The only way an unresolved intent is let go of — said out loud, and chosen. */}
        {unresolved ? (
          <button className="btn btn-ghost" id="siAbandon" type="button" onClick={onAbandon}>
            Bu isteği bırak
          </button>
        ) : null}
        <button className="btn btn-primary" id="siGo" type="button" disabled={busy} onClick={go}>
          {busy ? 'Oluşturuluyor…' : unresolved ? 'Aynı isteği yeniden dene' : 'Oluştur'}
        </button>
      </div>
    </Modal>
  );
}

// -------------------------------------------------------------------------------------- screen

/**
 * What to call a read that did not happen.
 *
 * Three failures, three sentences. A session that has gone is the shell's business and offers no
 * retry; a refusal is about the role, not the records; anything else is the server or the network and
 * is worth trying again.
 */
function registerFailureTitle(failure: ReadFailure | null): string {
  if (failure?.kind === 'session') return 'Oturum sona erdi — kayıtlar okunamadı.';
  if (failure?.kind === 'forbidden') return 'Bu kayıtları görme yetkiniz yok.';
  return 'Kayıtlar yüklenemedi.';
}

export function CredentialsScreen(): ReactElement {
  const db = useDemoData();
  const queries = useV07Queries();
  const commands = useV07Commands();
  const role = useV07Role();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const search = params.toString();

  const [filters, setFilters] = useState({ ...LIST });
  const [applied, setApplied] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  /*
   * `project.export` is owner and admin. Drawing the control for the other two would be offering a
   * button whose only answer is 403 — the screen asks the same question the server will.
   */
  const canExport = role === 'owner' || role === 'admin';
  /* `claim.resend` — A·A·A·D. A viewer is not offered a send it would be refused. */
  const mayResend = role === null || role === 'owner' || role === 'admin' || role === 'operator';
  const [revokeCode, setRevokeCode] = useState<string | null>(null);
  const [resendCode, setResendCode] = useState<string | null>(null);
  /*
   * Which records are selected for a first send, BY CREDENTIAL ID.
   *
   * Ids, never addresses and never names: one person can hold two certificates, and selecting «that
   * person» would quietly join two different records into one act. The set survives paging, so an
   * operator can tick rows on page one and page two and send them together.
   *
   * Only ever populated while the «e-posta gönderilmemiş» filter is on, which is the only state
   * where the controls exist.
   */
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [firstSendScope, setFirstSendScope] = useState<'picked' | 'filter' | null>(null);
  /**
   * Which scope the ACCEPTANCE-LINK act is open for, if any.
   *
   * Its own state rather than a fourth value on `sendKind`: creating a link is not a send — it
   * queues no message and reaches no provider — and sharing the send's control would make the two
   * acts look like variants of one. The scopes are the same two, named the same way.
   */
  const [claimLinkScope, setClaimLinkScope] = useState<'picked' | 'filter' | null>(null);
  /*
   * WHICH ACT the bar's two scope buttons will perform.
   *
   * One control rather than six buttons, and the act is named in the buttons as well as in the
   * window that opens, because «gönder» over a scope is three different things and an operator who
   * reads only the scope has not been told which one they are about to do. The first send is the
   * default: it is the act the bar existed for before the other two, and the one «e-posta
   * gönderilmemiş» leads to.
   */
  const [sendKind, setSendKind] = useState<SendRequestKind>('first_send');
  /** Which membership change is being confirmed, and over which of the two named scopes. */
  const [groupAction, setGroupAction] = useState<{
    direction: 'add' | 'remove';
    scope: 'picked' | 'filter';
  } | null>(null);
  /** The group chosen in that dialog, and the name being typed in «Yeni grup». */
  const [groupChoice, setGroupChoice] = useState('');
  const [newGroup, setNewGroup] = useState<string | null>(null);
  const [groupBusy, setGroupBusy] = useState(false);
  /**
   * The name of the send the resend window is about to make, minted when the window OPENS.
   *
   * Every press inside one opening is the same send, so the server can answer a retry with the
   * message it already queued; opening the window again is a new intent and mints a new name, which
   * is exactly the deliberate second send an operator is entitled to make.
   */
  const [resendKey, setResendKey] = useState(() => newIdempotencyKey());
  const account = useV07Account();
  const ownerKey = account?.ownerKey ?? null;
  /*
   * An intent this tab was still waiting on when it was last painted.
   *
   * Read once, before either piece of state exists, so the register and the window agree: if there
   * is one, the window reopens on it rather than the register looking ordinary while an unfinished
   * request sits in storage.
   */
  const [storedIntent] = useState(() => readPendingIntent(ownerKey));
  const restored = storedIntent.kind === 'intent' ? storedIntent.intent : null;
  /*
   * A request that left this tab and whose record cannot be used any more.
   *
   * Not «nothing here»: somebody pressed «Oluştur», the server may well have made the document, and
   * the local copy is now damaged or past the twelve hours it may hold a name and an address for.
   * The register is where that is settled, and the operator is told so instead of being handed a
   * clean form and a new key as though the earlier request had never happened.
   */
  const [lostIntent, setLostIntent] = useState(storedIntent.kind === 'unusable' ? storedIntent : null);
  const [issueOpen, setIssueOpen] = useState(restored !== null);
  /*
   * The intent, which outlives the WINDOW.
   *
   * The key was minted when the dialog opened, so closing it — Escape, the ✕, «Vazgeç», a backdrop
   * click — threw the intent away, and reopening the same form minted a new one. That is exactly the
   * moment the idempotency contract exists for: if the first attempt reached the server, a second
   * key asks it, correctly, for a SECOND document.
   *
   * So an intent whose outcome is UNRESOLVED — the request never reached the backend, or its answer
   * never came back — is kept here, and the dialog resumes it: same key, same values. A definite
   * refusal clears it, because then the server told us nothing was created.
   */
  /*
   * …and outlives the SCREEN, for the person who started it.
   *
   * React state dies with the component, so leaving the register or pressing reload threw the
   * intent away just as surely as closing the window did, and the next attempt minted a new key for
   * work the server may already have done. `lib/pending-intent.ts` keeps it in this tab's own
   * storage, bound to this user, stamped so it cannot linger, and carrying no token of any kind.
   */
  const [pendingIssue, setPendingIssue] = useState<IssueResult | null>(
    restored
      ? {
          templateId: restored.templateId,
          releaseId: restored.releaseId,
          claims: restored.claims,
          email: restored.email,
          issueDate: restored.issueDate,
          expiresAt: restored.expiresAt,
          lang: restored.lang,
          sendEmail: restored.sendEmail,
          idempotencyKey: restored.idempotencyKey,
          startedAt: restored.startedAt,
        }
      : null,
  );
  /* The intent an in-flight answer belongs to, readable from inside a promise that may be stale. */
  const pendingRef = useRef<IssueResult | null>(pendingIssue);
  /* Whether this tab could keep the intent past a reload — a restored one clearly could. */
  const [intentPersisted, setIntentPersisted] = useState(restored !== null);
  /*
   * How many attempts at the CURRENT intent are still waiting for an answer.
   *
   * A double click sends the same intent twice, and the backend answers the second one «a request
   * with this key is in progress» — a refusal, but emphatically NOT «nothing was created»: it means
   * the operator's other attempt is still running. Treating it like any other refusal ended the
   * intent while the real one was still in flight, so the answer that mattered arrived to find
   * nothing waiting for it and the register never opened the document it had just made.
   */
  const inFlight = useRef(0);
  /*
   * The store as of the latest paint, readable from inside a promise that outlived its render.
   *
   * A resumed intent has to find the release it NAMES, and the only way to look again after asking
   * the server to re-read is to see the store the refresh produced rather than the one this closure
   * captured.
   */
  const latestTemplates = useRef(db.templates);
  latestTemplates.current = db.templates;

  /*
   * The query string is read on entry, exactly the way `Screens.credentials` reads it: a parameter
   * that is ABSENT leaves the control alone, which is why arriving from the panel's «Gör» link
   * with only `?status=` set does not also clear the search box. Applying it during render rather
   * than in an effect means the first paint already shows the filtered list.
   */
  if (applied !== search) {
    const q = new URLSearchParams(search);
    if (q.get('status') !== null) LIST.status = q.get('status') ?? '';
    if (q.get('q') !== null) LIST.q = q.get('q') ?? '';
    if (q.get('template')) LIST.template = q.get('template') ?? '';
    LIST.page = 1;
    setApplied(search);
    setFilters({ ...LIST });
  }

  /* `delivery` and `batch` have no control of their own: they arrive in the URL and leave with
     «filtreyi kaldır», so they are read straight from it rather than mirrored into `LIST`. */
  const delivery = params.get('delivery') || null;
  const batch = params.get('batch') || null;
  /*
   * «E-posta gönderilmemiş» — in the URL, like the two above, and for the same reason: it arrives
   * from a link rather than from a control, so the toolbar the reference froze is untouched. With
   * it off, this screen is exactly what it was, which is also why the review surface is unchanged.
   *
   * It is NOT `delivery=none`. That one is «no send at all», so a record whose one e-mail failed
   * does not match it although nothing ever reached the holder; this asks whether an e-mail exists
   * at all, whatever became of it.
   */
  const noEmail = params.get('noEmail') === '1';
  /*
   * ONE GROUP'S MEMBERS (migration 028) — in the URL like `batch` and `delivery`, and written by
   * the toolbar's own control below.
   *
   * It is NOT `batch`: that one is where a record came from and cannot change, this one is where an
   * operator put it. They compose, so «this upload, in this group» is one page.
   */
  const group = params.get('group') || null;
  /*
   * SELECTION, turned on deliberately.
   *
   * The tick column used to appear only with «e-posta gönderilmemiş», because that was the only
   * thing a selection was for. Grouping and re-sending need it too, and tying it to a filter would
   * mean «to put records in a group, first filter by something else». So there is a control, and
   * with it OFF this register is exactly what the reference froze — the column, the bar and the
   * header tick do not exist.
   */
  const selectMode = params.get('select') === '1';

  /*
   * «E-posta gönderilmemiş» — a real control now, writing the same URL parameter.
   *
   * It keeps the other parameters the address already carries (a batch link, a delivery link), so
   * the three narrow the register together rather than replacing one another, and it resets the
   * page because the first page of a different question is page one.
   */
  /** One URL parameter at a time, keeping the others: the filters narrow together. */
  const setParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    LIST.page = 1;
    setFilters({ ...LIST });
    setParams(next, { replace: false });
  };

  const setNoEmail = (on: boolean): void => {
    const next = new URLSearchParams(params);
    if (on) next.set('noEmail', '1');
    else next.delete('noEmail');
    LIST.page = 1;
    setFilters({ ...LIST });
    setParams(next, { replace: false });
  };

  const apply = (patch: Partial<RegisterState>): void => {
    Object.assign(LIST, patch);
    LIST.page = 1;
    setFilters({ ...LIST });
  };

  const goToPage = (n: number): void => {
    LIST.page = n;
    setFilters({ ...LIST });
    window.scrollTo({ top: 0 });
  };

  /**
   * «Dışa aktar» — the FILTERS, never the page.
   *
   * `filters.page` is deliberately not passed on: the endpoint refuses paging, because a file
   * bounded by the page the browser happened to be showing would quietly disagree with its own
   * title. The recipient's address is not asked for here; it is the one column that turns a
   * register into a mailing list and it needs its own decision, per request.
   */
  const runExport = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const r = await commands.exportCredentials({
        /*
         * EVERY filter the screen is standing in, serialised exactly as the group and send scopes
         * below serialise it. Three of these were missing, so the file disagreed with the screen
         * that produced it — and since the file now prints acceptance links and may mint one for
         * a record that has none, the disagreement wrote on records the operator never selected.
         */
        filter: {
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.template ? { templateId: filters.template } : {}),
          ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
          ...(batch ? { batchId: batch } : {}),
          ...(group ? { groupId: group } : {}),
          ...(delivery ? { delivery } : {}),
          ...(noEmail ? { noEmail: true } : {}),
        },
      });
      if (!r.ok || !r.body) {
        toast(r.message ?? 'Dışa aktarılamadı', 'danger');
        return;
      }
      downloadCsv(r.filename ?? 'hpw-credentials.csv', r.body);
      toast('Kayıt listesi indirildi', 'ok');
    } catch {
      /*
       * A THROW HERE USED TO BE THE ONLY OUTCOME NOBODY SAW.
       *
       * `exportCredentials` turns a refusal into `{ok: false}`, so the branch above reports what
       * the backend said. What it does not cover is anything thrown on THIS side of that call —
       * the writer below it, or the call itself failing before its own `try` — and this handler
       * had `try`/`finally` with no `catch`. The rejection went nowhere, `finally` put the button
       * back to «Dışa aktar», and the reader was left with no file and no sentence: exactly the
       * fault reported from production, where no file arrived and nothing was said.
       *
       * The same sentence as the refusal branch, deliberately: the reader's situation is the same
       * one — the file was not produced — and a second vocabulary for it would only be a second
       * thing to keep true.
       */
      toast('Dışa aktarılamadı', 'danger');
    } finally {
      setExporting(false);
    }
  };

  /*
   * The page, the count and the denominator all come from the same answer.
   *
   * They used to be computed from whatever the store had downloaded, which is how a register holding
   * two hundred rows reported «205 kayıt» as 200 and how its ninth page did not exist. The token is
   * the identity of the question; change a filter and a new read starts, and an answer to an older
   * question is dropped rather than painted (`useRead`), so a slow reply to «ay» cannot overwrite a
   * fast reply to «ayşe».
   */
  const registerToken = JSON.stringify([
    filters.q,
    filters.status,
    filters.template,
    delivery,
    batch,
    group,
    noEmail,
    filters.page,
    PER_PAGE,
  ]);
  const register = useRead(registerToken, () =>
    queries.registerPage({
      q: filters.q,
      status: filters.status,
      template: filters.template,
      delivery,
      batch,
      group,
      noEmail,
      page: filters.page,
      perPage: PER_PAGE,
    }),
  );
  /*
   * THE GROUPS, read once for the toolbar's own control — `credential.read`, the register's cell.
   *
   * On the revision signal like every other read here, so a group made or renamed in the dialog
   * below appears in the control without a reload.
   */
  const groups = useRead('credential-groups', () => queries.credentialGroups());
  const groupList = groups.value ?? [];
  const openGroup = groupList.find((g) => g.id === group) ?? null;
  /*
   * What the whole-filter scope IS, in the operator's words.
   *
   * «Filtreye uyan» is accurate but says nothing about which filter, and the one case where that
   * matters most is a group: acting from inside a group is how the brief asks for a send over one,
   * and the button must say the group's name rather than leave the operator to remember it. It is
   * still the same scope resolved by the same server — only the sentence changes.
   */
  const filterScopeWord = openGroup === null ? 'filtreye uyan' : `«${openGroup.name}» grubundaki`;
  const answer = register.value;
  const slice = answer?.rows ?? [];
  const pages = answer?.pages ?? 1;
  const page = answer?.page ?? Math.max(filters.page, 1);
  const matching = answer?.filtered ?? 0;
  const readable = answer?.total ?? matching;

  /*
   * The selection column exists only where the operation does.
   *
   * It is drawn when the «e-posta gönderilmemiş» filter is on AND the role may send, so with the
   * filter off the register has exactly the columns the frozen reference measures — the visual
   * acceptance of this screen is not a thing this round negotiates with.
   */
  const selecting = (noEmail || selectMode) && mayResend;
  /**
   * THE TWO SCOPES, as the server reads them — one place, because two acts now send them.
   *
   * «picked» hands over the ids the operator ticked. «filter» hands over the filter ITSELF,
   * including `noEmail`, so the server resolves the target set: the page the browser happens to be
   * showing is not the scope and is never silently used as one. It is the register's own state
   * rather than a constant — the bar is reachable with the e-mail filter off, so sending `true`
   * would describe a scope the operator is not looking at.
   */
  const scopeBody = (which: 'picked' | 'filter'): FirstSendScopeBody =>
    which === 'picked'
      ? { credentialIds: [...picked] }
      : {
          filter: {
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.template ? { templateId: filters.template } : {}),
            ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
            ...(batch ? { batchId: batch } : {}),
            ...(group ? { groupId: group } : {}),
            /*
             * The delivery filter belongs in the scope for the same reason every other one does:
             * the button's number is the register's own filtered total, and the register counts
             * with this term. Leaving it out made the two disagree in the one direction that
             * matters — the label said «filtreye uyan 3 kayıt» and the request named every
             * record the OTHER filters matched. The dashboard links straight into it
             * (`?delivery=bounced`, `?delivery=pending`), so it is one click away, and the export
             * and grouping scopes a few lines down have always carried it.
             */
            ...(delivery ? { delivery } : {}),
            ...(noEmail ? { noEmail: true } : {}),
          },
        };
  const cols = selecting ? 10 : 9;
  /*
   * Leaving the filter drops the selection.
   *
   * Without this, «filtreyi kaldır» hides the bar while the ticks stay in state, and coming back to
   * the filter later would present a selection the operator made in another sitting as if it were
   * current. A selection only means something where it can be acted on.
   */
  useEffect(() => {
    if (!selecting) setPicked((prev) => (prev.size === 0 ? prev : new Set()));
  }, [selecting]);

  /*
   * AND CHANGING WHAT THE REGISTER IS NARROWED TO DROPS IT AS WELL.
   *
   * Same principle as the reset above, applied to the case that reaches further: the ticks are
   * credential ids and the narrowing is a search param, so switching group, status, template,
   * batch, delivery or the search text in place left the SET verbatim while the rows under it
   * changed completely. Nothing remounted the screen (`setParam` copies the existing params, so
   * `select=1` survives and the route carries no key), so the act stayed enabled and labelled off
   * records the operator could no longer see — «Yeniden gönderim — seçili 12 kayıt» over a group
   * they had just left. The confirmation could not reveal it either: the preview is counts and
   * per-reason skips, and the table that names each person is drawn only AFTER the send is
   * accepted. The server is right to take the ids verbatim — a picked scope IS ids — so the one
   * honest place to fix it is here.
   *
   * Deliberately NOT keyed on the page: a selection that spans pages is the documented behaviour
   * and the only reason the ids are carried rather than the rows. This drops the selection when
   * the QUESTION changes, not when the operator pages through the same answer.
   */
  const narrowing = [
    filters.status,
    filters.template,
    filters.q.trim(),
    batch ?? '',
    group ?? '',
    delivery ?? '',
    noEmail ? '1' : '0',
  ].join('|');
  const narrowedTo = useRef(narrowing);
  useEffect(() => {
    if (narrowedTo.current === narrowing) return;
    narrowedTo.current = narrowing;
    setPicked((prev) => (prev.size === 0 ? prev : new Set()));
  }, [narrowing]);

  /*
   * The page joins the store on its way past, so the dialogs, the command layer and the reference's
   * own helpers keep finding records where they have always looked for them.
   */
  useAdoptCredentials(answer?.rows ?? null);

  /* The filtered batch, for the note above the table. Only read while that filter is on. */
  const batchRead = useRead(`batch:${batch ?? ''}`, () =>
    batch === null ? ({ kind: 'not_found' } as BatchLookup) : queries.batchById(batch),
  );
  const bt = batchRead.value?.kind === 'found' ? batchRead.value.batch : null;
  /* The dialogs are always opened from a row that is on screen, so the page is where to find it. */
  const revoking = revokeCode ? (slice.find((c) => c.publicCode === revokeCode) ?? null) : null;
  const resending = resendCode ? (slice.find((c) => c.publicCode === resendCode) ?? null) : null;
  const published = publishedTemplates(db);

  /*
   * One text node per run: the count line is built as a string up to each link it contains.
   *
   * And it states a NUMBER only when a number arrived. «0 kayıt» over a read that failed is the same
   * lie as an empty table over one: it reports a server problem as an empty register.
   */
  let lead =
    answer === null
      ? register.status === 'error'
        ? 'Kayıtlar okunamadı'
        : 'Yükleniyor…'
      : `${fmtNum(matching)} kayıt`;
  /*
   * The denominator, and only when a filter is narrowing the set.
   *
   * Unfiltered the two numbers are the same and the reference prints one of them, so the line stays
   * exactly what the reference prints. Filtered, «12 kayıt» alone cannot be told apart from a
   * register that holds twelve records.
   */
  if (answer !== null && matching !== readable) lead += ` · toplam ${fmtNum(readable)}`;
  if (delivery) lead += ` · e-posta: ${deliveryPill(delivery)[0]}`;
  if (noEmail) lead += ' · e-posta gönderilmemiş';
  /*
   * The group the page is narrowed to, by NAME.
   *
   * And a group the read has not answered for yet, or one that is gone, says so rather than
   * printing an id: a line that reads «grup: 7f3a…» is the register telling the operator nothing.
   */
  if (group) lead += ` · grup: ${openGroup ? openGroup.name : groups.status === 'error' ? 'okunamadı' : '…'}`;
  const countNodes: ReactNode[] = [];
  if (bt) {
    countNodes.push(`${lead} · batch: `);
    countNodes.push(
      <Link key="batch" to={`/admin/import?batch=${bt.id}`}>
        {bt.filename}
      </Link>,
    );
    countNodes.push(' · ');
    countNodes.push(
      <Link key="clear" to="/admin/credentials">
        filtreyi kaldır
      </Link>,
    );
  } else if (delivery || noEmail || group) {
    countNodes.push(`${lead} · `);
    countNodes.push(
      <Link key="clear" to="/admin/credentials">
        filtreyi kaldır
      </Link>,
    );
  } else {
    countNodes.push(lead);
  }

  const openIssue = (): void => {
    /* Rule 3: a credential may only be produced from a published, immutable release. */
    if (published.length === 0) {
      toast('Önce bir şablon yayınlayın (editördeki yayın kapısı)');
      return;
    }
    setIssueOpen(true);
  };

  const finishRevoke = async (reason: string | null): Promise<void> => {
    const code = revokeCode;
    setRevokeCode(null);
    if (reason === null || code === null) return;
    /* The reference closes the dialog and reports the outcome either way; a reason under three
       characters is refused by the command, not by the form. */
    const r = await commands.revokeCredential({ code, reason });
    toast(
      r.ok
        ? 'Kayıt iptal edildi — public sayfa «İptal edildi» gösteriyor'
        : (r.message ?? 'İptal edilemedi: bu durumdan geçiş yok'),
    );
  };

  const finishResend = async (reason: string | null): Promise<void> => {
    const code = resendCode;
    const rotated = resending?.state === 'pending_acceptance';
    const rotation = resending ? (claimTokenView(resending)?.rotationCount ?? 0) + 1 : 1;
    setResendCode(null);
    if (reason === null || code === null) return;
    const r = await commands.resendClaimEmail({ code, reason, idempotencyKey: resendKey });
    if (!r.ok) {
      toast(r.message ?? 'Gönderilemedi');
      return;
    }
    toast(
      rotated
        ? `Yeni onay bağlantısı üretildi ve gönderim kuyruğuna alındı · rotasyon ${rotation}`
        : 'Bilgilendirme e-postası gönderim kuyruğuna alındı',
    );
  };

  /*
   * The act, performed for the DIALOG, which stays open until it lands.
   *
   * It used to close first and run afterwards, so a refusal left no form to correct and no way to
   * retry the same intent — and every retry minted a new idempotency key for work the server may
   * already have done.
   */
  const finishIssue = async (
    result: IssueResult,
  ): Promise<{ ok: boolean; message?: string; code?: string; outcome?: 'refused' | 'unresolved' }> => {
    /* Every template, not only the published ones: the release a pending intent names may sit on a
       template that has since been archived, and that is still the release it was sent with. */
    const template = latestTemplates.current.find((t) => t.id === result.templateId);
    /*
     * The release the intent NAMES, not whatever is current now.
     *
     * A retry repeats the first attempt. Re-selecting `currentRelease` here meant that a release
     * published between the first attempt and the retry was silently substituted under the same
     * idempotency key — the one thing a retry must never do. A fresh intent carries the current
     * release already, because that is what the dialog validated against.
     */
    let release = template?.releases.find((r) => r.id === result.releaseId) ?? null;
    if (release === null) {
      /*
       * Not found locally — so ask the server, through the read the console already uses.
       *
       * There used to be a `?? currentRelease(template)` here, which substituted a DIFFERENT
       * release under the first attempt's idempotency key: the retry then was a different request
       * wearing the first one's name. There is no acceptable substitute, so the projection is
       * refreshed once and, if the named release still is not there, the intent is kept and the
       * operator is told — which is the only honest answer, because sending anything else would be
       * making up content for a request somebody already made.
       */
      await commands.refresh();
      const again = latestTemplates.current.find((t) => t.id === result.templateId);
      release = again?.releases.find((r) => r.id === result.releaseId) ?? null;
    }
    if (release === null)
      return {
        ok: false,
        outcome: 'unresolved',
        message:
          'Bu isteğin yapıldığı release okunamadı. Aynı istek başka bir release ile gönderilemez; istek korunuyor.',
      };
    /*
     * Held from the first attempt on, so the window may close — and the screen may be left, or
     * reloaded — without the intent going with it.
     */
    setPendingIssue(result);
    pendingRef.current = result;
    /*
     * Whether this tab can really hand the intent back after a reload.
     *
     * Storage can be full, blocked, or off entirely. The window and the retry work regardless —
     * the intent is in the screen — but only a write that actually landed lets us tell somebody
     * their unfinished request will still be here if they refresh, so the answer is kept and the
     * dialog says the one thing that is true.
     */
    setIntentPersisted(
      ownerKey
        ? writePendingIntent({
            idempotencyKey: result.idempotencyKey,
            templateId: result.templateId,
            releaseId: release.id,
            claims: result.claims,
            email: result.email,
            issueDate: result.issueDate,
            expiresAt: result.expiresAt,
            lang: result.lang,
            sendEmail: result.sendEmail,
            ownerKey,
            /* The FIRST attempt's stamp, never today's: the window belongs to the request. */
            startedAt: result.startedAt,
          })
        : false,
    );
    /* The demo mints the code so it can navigate; the product navigates to the one it is told. */
    inFlight.current += 1;
    let r: Awaited<ReturnType<typeof commands.issueCredential>>;
    try {
      r = await commands.issueCredential({
        release,
        claims: result.claims,
        email: result.email,
        issueDate: result.issueDate,
        expiresAt: result.expiresAt || null,
        lang: result.lang,
        sendEmail: result.sendEmail,
        publicCode: mintPublicCode(db),
        idempotencyKey: result.idempotencyKey,
      });
    } finally {
      inFlight.current -= 1;
    }
    /*
     * Whether this answer is still the one being waited for.
     *
     * An answer that arrives after the operator abandoned the intent, or after a later attempt
     * settled, belongs to nothing on screen. Acting on it anyway closed a window the operator had
     * just opened and navigated them to a record they were not looking at. The key identifies the
     * intent, so a stale answer is recognisable and is reported rather than obeyed.
     */
    const current = () => pendingRef.current?.idempotencyKey === result.idempotencyKey;
    if (!r.ok || !r.publicCode) {
      /*
       * Which refusals PROVE that nothing was created — and which ones prove the opposite.
       *
       * Two of the backend's own codes are refusals of this attempt that are evidence of an earlier
       * one. `idempotency_in_flight` means another attempt at this very intent is still running;
       * `idempotency_key_reused_with_different_request` means the key has already COMPLETED, with a
       * different body. Clearing the intent on either is the worst possible reading: the first
       * throws away the intent while the attempt that matters is still in the air, and the second
       * discards the only handle on a document that demonstrably exists.
       */
      const provesAnEarlierAttempt =
        r.code === 'idempotency_in_flight' || r.code === 'idempotency_key_reused_with_different_request';
      /* A refusal is the server's answer, so the intent is over; an unresolved one is not. */
      /* …and only when nothing else is still trying: see `inFlight` above. */
      if (r.outcome !== 'unresolved' && !provesAnEarlierAttempt && current() && inFlight.current === 0) {
        setPendingIssue(null);
        pendingRef.current = null;
        clearPendingIntent();
      }
      return {
        ok: false,
        ...(r.message ? { message: r.message } : {}),
        ...(r.code ? { code: r.code } : {}),
        ...(r.outcome ? { outcome: r.outcome } : {}),
      };
    }
    if (!current()) {
      /*
       * It succeeded, late. The document is real and the operator is told where it is, but nothing
       * on screen is disturbed: the window they are in stays open and they are not taken away.
       */
      toast(`Önceki istek tamamlanmış · kayıt ${r.publicCode}`);
      await commands.refresh();
      return { ok: true };
    }
    setPendingIssue(null);
    pendingRef.current = null;
    clearPendingIntent();
    setIssueOpen(false);
    toast(`Kayıt oluşturuldu · durum: ${STATUS_TR[stateForPolicy(release.publicationPolicy)]}`);
    void navigate(`/admin/credentials/${r.publicCode}`);
    return { ok: true };
  };

  return (
    <section data-screen="credentials">
      {/*
       * A request that left this tab and cannot be retried — said out loud, on the register.
       *
       * This is the surface the `readPendingIntent` comment always promised and the product never
       * had: «go and look at the register». The record itself is gone (damaged, or past the twelve
       * hours it may hold a name and an address for) and the breadcrumb carries no personal data —
       * the key, the template and when it started, so the operator can reconcile rather than guess.
       * It cannot be resumed, because a retry would be a request reconstructed from nothing, which
       * is exactly what the idempotency key exists to forbid.
       */}
      {lostIntent === null ? null : (
        <div className="warn-box" id="credLostIntent" role="alert" style={{ marginBottom: 14 }}>
          <b>Sonucu bilinmeyen bir üretim isteği vardı.</b>
          {lostIntent.trace === null
            ? ' Bu tarayıcıdaki kaydı okunamadığı için ayrıntısı kurtarılamadı.'
            : ` ${
                lostIntent.trace.reason === 'expired'
                  ? 'Kaydı 12 saatlik saklama sınırını geçtiği için kişisel verisi silindi.'
                  : 'Bu tarayıcıdaki kaydı bozulduğu için kullanılamıyor.'
              }`}
          {
            ' Kayıt oluşmuş olabilir; aynı istek yeniden gönderilemez, çünkü gövdesi artık elimizde değil. Kayıt defterinden kontrol edin.'
          }
          {lostIntent.trace === null ? null : (
            <div className="small" style={{ marginTop: 6 }}>
              <span className="mono" data-lost-key>
                {lostIntent.trace.idempotencyKey}
              </span>
              {` · şablon `}
              <span className="mono">{lostIntent.trace.templateId}</span>
              {` · başlangıç ${fmtDateTime(lostIntent.trace.startedAt, 'tr')}`}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              type="button"
              id="credLostIntentAck"
              onClick={() => {
                acknowledgeUnusableIntent();
                setLostIntent(null);
              }}
            >
              Anladım, gizle
            </button>
          </div>
        </div>
      )}
      <div className="page-head">
        <div>
          <span className="eyebrow">Kayıt defteri</span>
          <h1>Rozet ve Sertifikalar</h1>
          {/* Written as a string rather than JSX text so no reformatting can re-flow the copy or
              split the run — the whole paragraph is one text node, as in the reference. */}
          <p className="lead">
            {
              'Esas kayıt burada tutulur; görsel yalnız sunum biçimidir. Durum tarihlerden hesaplanır: alıcı onaylamadan «onay bekliyor», iptal edilen ya da süresi dolan kayıtlar silinmez.'
            }
          </p>
        </div>
        <div className="page-actions">
          {/*
           * «Dışa aktar» — the filtered register, as a file (PO 7.12 §1).
           *
           * IN THE PAGE'S OWN ACTION GROUP, and that placement is the measured fix rather than a
           * preference. It began at the end of `.toolbar`, where the reference's row holds the
           * search, the two filters and the count — and the reference's row, measured, is ONE row
           * at 1440 and at 1280. With this button appended the product's row needed 1077px of the
           * 980 a 1280 screen gives it, so it wrapped and the button landed alone at the start of a
           * second line (measured: toolbar 38px → 77px at 1280, the button at x=268,y=223). Moving
           * it here takes 97px out of that row, which restores the reference's composition at both
           * widths, and it puts a whole-list action where the design already keeps whole-list
           * actions: beside «Toplu Üretim» and «+ Tek Credential», secondary before primary.
           *
           * Drawn for owner and admin only, because `project.export` is their cell and a control
           * that answers 403 is worse than no control. What leaves is what the FILTERS say, never
           * the page: the server pages through the rest.
           */}
          {canExport ? (
            <button
              className="btn btn-secondary"
              type="button"
              id="credExport"
              disabled={exporting}
              onClick={() => void runExport()}
            >
              {exporting ? 'Hazırlanıyor…' : 'Dışa aktar'}
            </button>
          ) : null}
          <Link className="btn btn-secondary" to="/admin/import">
            Toplu Üretim
          </Link>
          {/*
           * `credential.issue` is A·A·A*·D in the authorization matrix, so a viewer is never
           * offered it. Not authorization — the backend refuses a viewer's POST either way — but
           * §11 is explicit that a control is only shown to a role that may use it, and a button
           * that exists to be refused is the decorative button it forbids.
           *
           * The demo has no session and therefore no role, and the reference's own screen shows
           * the control; `role === null` keeps it, which is the state the prototype is in.
           */}
          {role === 'viewer' ? null : (
            <button className="btn btn-primary" type="button" id="singleIssueBtn" onClick={openIssue}>
              + Tek Credential
            </button>
          )}
        </div>
      </div>
      <div className="toolbar">
        <input
          className="input search"
          id="credSearch"
          placeholder="Ad, şirket, kod veya e-posta ara…"
          autoComplete="off"
          aria-label="Ara"
          value={filters.q}
          onChange={(e) => apply({ q: e.target.value })}
        />
        <select
          className="select"
          id="credStatus"
          aria-label="Durum"
          value={filters.status}
          onChange={(e) => apply({ status: e.target.value })}
        >
          <option value="">Tüm durumlar</option>
          <option value="pending_acceptance">Onay bekliyor</option>
          <option value="active">Aktif</option>
          <option value="expired">Süresi dolmuş</option>
          <option value="revoked">İptal edilmiş</option>
        </select>
        <select
          className="select"
          id="credTemplate"
          aria-label="Şablon"
          value={filters.template}
          onChange={(e) => apply({ template: e.target.value })}
        >
          <option value="">Tüm şablonlar</option>
          {db.templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        {/*
         * The filter the first send turns on, in the toolbar rather than only in the address.
         *
         * A select, because the two controls beside it are selects and this is the same kind of
         * question. It is NOT the «E-posta» column's filter: that one is the STATE of the record's
         * most recent send, so a record whose one e-mail failed is not «no send» to it. This asks
         * whether any e-mail exists for the record at all, whatever became of it.
         *
         * This is the one control this round adds to a frozen screen, and it carries its own
         * approved reference correction.
         */}
        <select
          className="select"
          id="credEmail"
          aria-label="E-posta durumu"
          value={noEmail ? 'none' : ''}
          onChange={(e) => setNoEmail(e.target.value === 'none')}
        >
          <option value="">Tüm kayıtlar</option>
          <option value="none">E-posta gönderilmemiş</option>
        </select>
        {/*
         * THE GROUP FILTER (migration 028) — a fourth select, beside the three that are already here.
         *
         * Its options are the groups that exist, by name, so «gruba göre filtreleme» is a control
         * rather than an address the operator has to type. A failed read says so in the option
         * rather than drawing an empty list, which would read as «there are no groups».
         *
         * Drawn only once there is a group to filter by, or while one is being filtered on. A
         * select whose only option is «Tüm gruplar» filters nothing, and a deployment that has
         * never made a group therefore has the toolbar it always had.
         */}
        {mayResend && (groupList.length > 0 || group !== null) ? (
          <select
            className="select"
            id="credGroup"
            aria-label="Grup"
            value={group ?? ''}
            onChange={(e) => setParam('group', e.target.value === '' ? null : e.target.value)}
          >
            <option value="">Tüm gruplar</option>
            {groups.status === 'error' ? <option value="">Gruplar okunamadı</option> : null}
            {groupList.map((g) => (
              <option key={g.id} value={g.id}>
                {`${g.name} (${fmtNum(g.memberCount)})`}
              </option>
            ))}
          </select>
        ) : null}
        {/*
         * «Seç» — the tick column, turned on deliberately.
         *
         * Off, this register has the columns the frozen reference measures. On, every row gets a
         * tick and the action bar appears; the two scopes it offers are named rather than implied,
         * which is the whole point of the control existing.
         */}
        {mayResend ? (
          <button
            className={`btn ${selectMode ? 'btn-primary' : 'btn-secondary'} btn-sm`}
            type="button"
            id="credSelectMode"
            aria-pressed={selectMode}
            onClick={() => setParam('select', selectMode ? null : '1')}
          >
            {selectMode ? 'Seçimi kapat' : 'Seç'}
          </button>
        ) : null}
        <span className="muted small" id="credCount">
          {countNodes}
        </span>
      </div>
      {/*
       * THE FIRST-SEND BAR, and only while «e-posta gönderilmemiş» is on.
       *
       * Two scopes, named rather than implied, because the difference between them is the thing an
       * operator most needs to see: the records they ticked, or every record the filter matches —
       * which is not the same as the page they are looking at. The counts say which is which.
       *
       * With the filter off none of this exists, so the register is exactly what the reference
       * froze.
       */}
      {selecting ? (
        <div className="card" id="fsBar">
          {/*
           * ONE padded body, three rows — the approved reference's own shape
           * (`docs/frontend/media/credentials-reference-2026-10-15/rozet-ve-sertifikalar.html`:
           * `.actions{padding:16px 20px;display:flex;flex-direction:column;gap:12px}` with three
           * `.row` children). The three rows used to be three separately padded blocks, which drew
           * the panel's padding three times and put forty pixels between rows where the reference
           * puts twelve. The rows keep their ids, so nothing that addresses them had to move.
           */}
          <div className="card-pad">
            <div className="toolbar" id="fsBarRow">
              <span className="muted small" id="fsBarCount">
                {picked.size > 0
                  ? `${fmtNum(picked.size)} kayıt seçildi`
                  : `Seçim yok · ${filterScopeWord} ${fmtNum(matching)} kayıt`}
              </span>
              {/*
               * THE THREE ACTS, named (migration 029).
               *
               * Choosing the act and choosing the scope are two decisions and are made with two
               * controls; the window that opens then states both again, with the server's counts and
               * every record it will not send, before anything happens.
               */}
              <label className="muted small" htmlFor="fsBarKind">
                İşlem:
              </label>
              <select
                id="fsBarKind"
                className="select"
                value={sendKind}
                onChange={(e) => setSendKind(e.target.value as SendRequestKind)}
              >
                {SEND_REQUEST_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {SEND_ACTS[k].name}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-sm"
                type="button"
                id="fsBarPicked"
                disabled={picked.size === 0}
                onClick={() => setFirstSendScope('picked')}
              >
                {`${SEND_ACTS[sendKind].action} — seçili ${fmtNum(picked.size)} kayıt`}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="fsBarFilter"
                onClick={() => setFirstSendScope('filter')}
              >
                {`${SEND_ACTS[sendKind].action} — ${filterScopeWord} ${fmtNum(matching)} kayıt`}
              </button>
              {picked.size > 0 ? (
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  id="fsBarClear"
                  onClick={() => setPicked(new Set())}
                >
                  Seçimi temizle
                </button>
              ) : null}
            </div>
            {/*
             * GROUPING, on the same bar and with the same two scopes (migration 028).
             *
             * «Gruba ekle» is a membership write and nothing else: it issues nothing, changes no
             * certificate and sends no e-mail. It is offered for the ticked records and for everything
             * the filter matches, named separately, because those are different acts and the register
             * must not decide which one the operator meant.
             */}
            <div className="toolbar" id="grpBar">
              <span className="muted small">Gruplama:</span>
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="grpAddPicked"
                disabled={picked.size === 0}
                onClick={() => setGroupAction({ direction: 'add', scope: 'picked' })}
              >
                {`Seçili kayıtları gruba ekle (${fmtNum(picked.size)})`}
              </button>
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="grpAddFilter"
                onClick={() => setGroupAction({ direction: 'add', scope: 'filter' })}
              >
                {`Filtreye uyan tüm kayıtları gruba ekle (${fmtNum(matching)})`}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                id="grpRemovePicked"
                disabled={picked.size === 0}
                onClick={() => setGroupAction({ direction: 'remove', scope: 'picked' })}
              >
                {`Seçili kayıtları gruptan çıkar (${fmtNum(picked.size)})`}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                id="grpNew"
                onClick={() => setNewGroup('')}
              >
                Yeni grup
              </button>
            </div>
            {/*
             * ACCEPTANCE LINKS, on the same bar and with the same two scopes.
             *
             * A third row rather than a fourth act on the send selector, because this one does not
             * send: it produces a record's acceptance link and seals it, and the links are read
             * afterwards from «Dışa aktar». Offered only to the roles that may do BOTH halves — the
             * act rotates tokens (`claim.resend`) and its only product is a link that leaves the
             * building (`project.export`) — so an operator who could not download the file is not
             * shown a button whose answer would be 403.
             */}
            {canExport ? (
              <div className="toolbar" id="clBar">
                <span className="muted small">Kabul bağlantısı:</span>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="clBarPicked"
                  disabled={picked.size === 0}
                  onClick={() => setClaimLinkScope('picked')}
                >
                  {`Seçili kayıtlar için oluştur (${fmtNum(picked.size)})`}
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  type="button"
                  id="clBarFilter"
                  onClick={() => setClaimLinkScope('filter')}
                >
                  {`Filtreye uyan tüm kayıtlar için oluştur (${fmtNum(matching)})`}
                </button>
                <span className="muted small" id="clBarNote">
                  E-posta gönderilmez.
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="card" id="credTableCard">
        <div className="table-wrap">
          <table className="table" id="credTable">
            <thead>
              <tr>
                {selecting ? (
                  <th scope="col">
                    {/*
                     * «Bu sayfayı seç» — the PAGE, and the label says so.
                     *
                     * A header tick that silently meant «everything the filter matches» would be the
                     * defect the brief names: the browser holds one page and cannot tick what it has
                     * not read. Acting on the whole filtered set is a separate, named button on the
                     * bar above, where the server resolves the scope.
                     */}
                    <input
                      type="checkbox"
                      id="credPickPage"
                      aria-label={`Bu sayfadaki ${fmtNum(slice.length)} kaydı seç`}
                      title={`Bu sayfadaki ${fmtNum(slice.length)} kaydı seç`}
                      checked={slice.length > 0 && slice.every((c) => picked.has(c.id))}
                      onChange={(event) => {
                        const on = event.target.checked;
                        setPicked((prev) => {
                          const next = new Set(prev);
                          for (const c of slice) {
                            if (on) next.add(c.id);
                            else next.delete(c.id);
                          }
                          return next;
                        });
                      }}
                    />
                  </th>
                ) : null}
                <th>Sahip</th>
                <th>Şablon</th>
                <th>Veriliş</th>
                <th>Geçerlilik</th>
                <th>E-posta</th>
                <th>Durum</th>
                <th>LinkedIn Profil</th>
                <th>LinkedIn Paylaşım</th>
                <th scope="col">
                  <span className="sr-only">İşlemler</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {register.status === 'loading' ? (
                <tr>
                  <td colSpan={cols} className="muted" aria-busy="true">
                    Kayıtlar yükleniyor…
                  </td>
                </tr>
              ) : register.status === 'error' && slice.length === 0 ? (
                /*
                 * NOT «eşleşen kayıt yok». The register could not be read, which is a different fact
                 * and a different next step; saying the set is empty would report a server problem as
                 * a deletion.
                 */
                <tr>
                  <td colSpan={cols}>
                    <div className="warn-box" role="alert">
                      <b>{registerFailureTitle(register.error)}</b>
                      <br />
                      <span className="small">{register.error?.message ?? ''}</span>
                      {register.error?.kind === 'session' ? null : (
                        <>
                          {' '}
                          <button
                            className="btn btn-secondary btn-sm"
                            type="button"
                            id="credRetry"
                            onClick={register.retry}
                          >
                            Yeniden dene
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : slice.length === 0 ? (
                <tr>
                  <td colSpan={cols} className="muted">
                    Eşleşen kayıt yok.
                  </td>
                </tr>
              ) : (
                slice.map((c) => {
                  const t = db.templates.find((x) => x.id === c.templateId);
                  const s = credentialViewState(c);
                  const [deliveryText, deliveryClass] = deliveryPill(c.deliveryStatus);
                  /*
                   * Absent means «this surface never read it», not «nothing happened»: the demo has
                   * no engagement store, so its records answer «bilinmiyor» rather than a state
                   * nobody measured.
                   */
                  const e =
                    c.engagement ?? engagementFallback(c.subjectSnapshot.subjectType === 'organization');
                  const profileAdd = PROFILE_ADD_BADGE[e.profileAdd];
                  const shareBadge = SHARE_BADGE[e.share];
                  /* The post link is offered only where there IS one — a verified publication. */
                  const share = {
                    ...shareBadge,
                    hint:
                      e.share === 'published' && e.publishedAt
                        ? `${shareBadge.hint} · ${fmtDate(e.publishedAt.slice(0, 10), 'tr', 'short')}`
                        : shareBadge.hint,
                    href: e.share === 'published' ? e.postUrl : null,
                  };
                  return (
                    <tr
                      className="is-link"
                      data-code={c.publicCode}
                      key={c.id}
                      onClick={(event) => {
                        /* The row is a shortcut, not a trap: a click that landed on one of the
                           cell's own controls belongs to that control. */
                        if ((event.target as HTMLElement).closest('button,a')) return;
                        void navigate(`/admin/credentials/${c.publicCode}`);
                      }}
                    >
                      {selecting ? (
                        /*
                         * The tick is on the CREDENTIAL ID, never on a name or an address: two
                         * certificates of the same person are two records, and this is the column
                         * that keeps them two.
                         *
                         * `stopPropagation` on the cell, not a widened guard on the row: the row's
                         * «a click that landed on a control belongs to that control» rule is left
                         * exactly as it was for every other screen that relies on it.
                         */
                        <td
                          data-col="pick"
                          onClick={(event) => event.stopPropagation()}
                          onKeyUp={(event) => event.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={picked.has(c.id)}
                            aria-label={`${displayName(c)} kaydını seç`}
                            data-pick={c.publicCode}
                            onChange={(event) => {
                              const on = event.target.checked;
                              setPicked((prev) => {
                                const next = new Set(prev);
                                if (on) next.add(c.id);
                                else next.delete(c.id);
                                return next;
                              });
                            }}
                          />
                        </td>
                      ) : null}
                      <td>
                        <b style={{ fontWeight: 500 }}>{displayName(c)}</b>
                      </td>
                      <td data-col="template">
                        {/*
                         * The template's MANAGEMENT name, as text.
                         *
                         * The reference put the short code in a `.pill`, whose `::before` draws a
                         * 7px dot — a status marker on a label that carries no status. What a
                         * reader wants here is the name they gave the template, and what the table
                         * cannot afford is a column that grows with it. The LINES are decided in
                         * characters (`templateCellLines`: at most fourteen visible characters each,
                         * at most two, «…» inside the second one), each line is drawn as its own
                         * non-wrapping block so the browser has nothing left to re-decide, and the
                         * stylesheet caps the real pixel width beside them. The whole name stays in
                         * the DOM as the cell's title, and search, filter and the CSV export are the
                         * server's — none of them sees this clipping.
                         *
                         * An unreadable related template keeps the existing fallback — the template
                         * id — rather than a name invented here.
                         */}
                        <span
                          className="cred-tpl-name"
                          style={TEMPLATE_CELL_STYLE}
                          title={templateCellFull(t, c.templateId)}
                          {...(templateCellNameIsClipped(templateCellFull(t, c.templateId))
                            ? {
                                tabIndex: 0,
                                /*
                                 * The clip is in the TEXT now, so no amount of CSS can reveal the
                                 * rest of it: what a keyboard or a screen reader gets has to be
                                 * the whole name itself. `title` answers a pointer; this answers
                                 * everything else.
                                 */
                                'aria-label': templateCellFull(t, c.templateId),
                              }
                            : {})}
                        >
                          {templateCellLines(templateCellFull(t, c.templateId)).map((line, at) => (
                            <span
                              className="cred-tpl-line"
                              style={TEMPLATE_CELL_LINE_STYLE}
                              key={`${c.publicCode}-tpl-${String(at)}`}
                            >
                              {line}
                            </span>
                          ))}
                        </span>{' '}
                        <span className="small muted">
                          {`v${c.release.releaseNumber}${c.revision > 1 ? ` · r${c.revision}` : ''}`}
                        </span>
                      </td>
                      <td className="small tnum nowrap">{fmtDate(c.issueDate, 'tr', 'short')}</td>
                      <td className="small tnum nowrap">
                        {c.expiresAt ? fmtDate(c.expiresAt, 'tr', 'short') : 'süresiz'}
                      </td>
                      <td data-col="delivery">
                        <span className={`pill ${deliveryClass}`}>{deliveryText}</span>
                      </td>
                      <td data-col="state">
                        <span className={`pill ${STATUS_PILL[s]}`}>
                          {`${STATUS_ICON[s]} ${STATUS_TR[s]}`}
                        </span>
                      </td>
                      <td data-col="linkedin-profile">
                        <span className={`pill ${profileAdd.tone} sq`} title={profileAdd.hint}>
                          {profileAdd.label}
                        </span>
                      </td>
                      <td data-col="linkedin-share">
                        {share.href ? (
                          <a
                            className={`pill ${share.tone} sq`}
                            href={share.href}
                            target="_blank"
                            rel="noopener"
                            title={share.hint}
                          >
                            {share.label}
                          </a>
                        ) : (
                          <span className={`pill ${share.tone} sq`} title={share.hint}>
                            {share.label}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="actions">
                          <Link
                            className="icon-btn"
                            to={`/admin/credentials/${c.publicCode}`}
                            title="Detay"
                            aria-label="Detay"
                          >
                            <IconEye />
                          </Link>
                          <button
                            className="icon-btn"
                            type="button"
                            data-resend={c.publicCode}
                            title="E-postayı yeniden gönder"
                            aria-label="Yeniden gönder"
                            disabled={s === 'revoked'}
                            onClick={() => {
                              setResendKey(newIdempotencyKey());
                              setResendCode(c.publicCode);
                            }}
                          >
                            <IconSend />
                          </button>
                          <button
                            className="icon-btn"
                            type="button"
                            data-revoke={c.publicCode}
                            title="İptal et"
                            aria-label="İptal et"
                            disabled={s === 'revoked'}
                            onClick={() => setRevokeCode(c.publicCode)}
                          >
                            <IconX />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {pages > 1 ? (
              <tfoot>
                <tr>
                  <td colSpan={cols}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <span className="small muted">{`Sayfa ${page} / ${pages}`}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          type="button"
                          id="pgPrev"
                          disabled={page === 1}
                          onClick={() => goToPage(page - 1)}
                        >
                          ← Önceki
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          type="button"
                          id="pgNext"
                          disabled={page === pages}
                          onClick={() => goToPage(page + 1)}
                        >
                          Sonraki →
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </div>

      {revoking ? <RevokeDialog credential={revoking} onClose={finishRevoke} /> : null}
      {resending ? (
        <ResendDialog
          credential={resending}
          claimUrl={claimUrlStatus(db, resending)}
          onClose={finishResend}
        />
      ) : null}
      {issueOpen ? (
        <IssueDialog
          templates={published}
          resume={pendingIssue}
          persisted={intentPersisted}
          onSubmit={finishIssue}
          /* Closing is always allowed — Escape, the ✕, «Vazgeç» and the backdrop all land here — and
             it never resolves the intent. An unresolved one is still unresolved with the window
             shut, and reopening picks it up rather than starting a second one. */
          onCancel={() => setIssueOpen(false)}
          onAbandon={() => {
            /*
             * «Bu isteği bırak» stops US waiting. It does NOT cancel anything at the server and is
             * not told that it does: the record may well exist, which is why the operator is sent
             * to look rather than invited to create a second one.
             */
            setPendingIssue(null);
            pendingRef.current = null;
            clearPendingIntent();
            setIssueOpen(false);
            toast('İstek bırakıldı · sunucuda oluşmuş olabilir, kayıt defterini kontrol edin');
            void commands.refresh();
          }}
        />
      ) : null}
      {/*
       * ONE panel for both scopes, and for the other two surfaces as well.
       *
       * «picked» hands over the ids the operator ticked. «filter» hands over the filter itself,
       * including `noEmail`, so the server resolves the target set — the page the browser happens to
       * be showing is not the scope and is never silently used as one. The panel shows both numbers
       * before anything is sent.
       */}
      {/*
       * THE ACCEPTANCE-LINK WINDOW, over the same two scopes the bar names.
       *
       * It states the impact first and takes the destructive half's approval separately; nothing
       * is written until both are given. Closing it re-reads the register, because a record whose
       * token rotated is a record whose claim state on screen is already stale.
       */}
      {claimLinkScope !== null ? (
        <BulkClaimLinkPanel
          scope={scopeBody(claimLinkScope)}
          scopeLabel={
            claimLinkScope === 'picked'
              ? `${fmtNum(picked.size)} seçili kayıt`
              : `${filterScopeWord} ${fmtNum(matching)} kayıt`
          }
          onLinksCreated={register.retry}
          onClose={() => {
            setClaimLinkScope(null);
            register.retry();
          }}
        />
      ) : null}
      {firstSendScope !== null ? (
        <FirstSendPanel
          kind={sendKind}
          scope={scopeBody(firstSendScope)}
          /* The register's «E-posta» column is the same delivery, so the page is read again. */
          onDeliveryMoved={register.retry}
          what={
            firstSendScope === 'picked'
              ? `${fmtNum(picked.size)} seçili kayıt`
              : `${filterScopeWord} ${fmtNum(matching)} kayıt`
          }
          onClose={() => {
            setFirstSendScope(null);
            /* A finished send changes what the filter matches, so the register is asked again. */
            register.retry();
          }}
        />
      ) : null}
      {groupAction === null ? null : (
        <GroupMembershipDialog
          direction={groupAction.direction}
          scopeName={
            groupAction.scope === 'picked'
              ? `${fmtNum(picked.size)} seçili kayıt`
              : `filtreye uyan ${fmtNum(matching)} kayıt`
          }
          groups={groupList}
          chosen={groupChoice}
          onChoose={setGroupChoice}
          busy={groupBusy}
          onClose={() => setGroupAction(null)}
          onConfirm={() => {
            const groupId = groupChoice;
            if (!groupId) return;
            const scope =
              groupAction.scope === 'picked'
                ? { credentialIds: [...picked] }
                : {
                    filter: {
                      ...(filters.status ? { status: filters.status } : {}),
                      ...(filters.template ? { templateId: filters.template } : {}),
                      ...(filters.q.trim() ? { q: filters.q.trim() } : {}),
                      ...(batch ? { batchId: batch } : {}),
                      ...(group ? { groupId: group } : {}),
                      ...(delivery ? { delivery } : {}),
                      ...(noEmail ? { noEmail: true } : {}),
                    },
                  };
            setGroupBusy(true);
            const run =
              groupAction.direction === 'add'
                ? commands.addCredentialsToGroup({ groupId, scope })
                : commands.removeCredentialsFromGroup({ groupId, scope });
            void run
              .then((r) => {
                toast(r.message ?? (r.ok ? 'Grup güncellendi' : 'Grup güncellenemedi'));
                if (r.ok) {
                  setGroupAction(null);
                  /* The page and the group sizes both changed, so both are asked again. */
                  register.retry();
                  groups.retry();
                }
              })
              .finally(() => setGroupBusy(false));
          }}
        />
      )}
      {newGroup === null ? null : (
        <Modal label="Yeni grup" onClose={() => setNewGroup(null)}>
          <ModalHead title="Yeni grup" onClose={() => setNewGroup(null)} />
          <div className="card-pad stack" style={{ gap: 10 }}>
            <label className="field" htmlFor="grpNewName">
              Grup adı
              <input
                className="input"
                id="grpNewName"
                value={newGroup}
                maxLength={160}
                placeholder="Örn. CXO 100 · Ekim"
                onChange={(e) => setNewGroup(e.target.value)}
              />
            </label>
            <div className="help">
              Grup yalnız kayıtları bir arada adlandırır. Sertifika üretmez, var olan sertifikayı değiştirmez
              ve e-posta göndermez. Grubu sonradan kaldırmak kayıtları silmez.
            </div>
          </div>
          <div className="card-foot">
            <button className="btn btn-ghost" type="button" onClick={() => setNewGroup(null)}>
              Vazgeç
            </button>
            <button
              className="btn btn-primary"
              type="button"
              id="grpNewSave"
              disabled={groupBusy || newGroup.trim() === ''}
              onClick={() => {
                setGroupBusy(true);
                void commands
                  .createCredentialGroup({ name: newGroup })
                  .then((r) => {
                    toast(r.ok ? `«${r.message ?? ''}» grubu oluşturuldu` : (r.message ?? 'Oluşturulamadı'));
                    if (r.ok) {
                      setNewGroup(null);
                      groups.retry();
                    }
                  })
                  .finally(() => setGroupBusy(false));
              }}
            >
              {groupBusy ? 'Oluşturuluyor…' : 'Grubu oluştur'}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}

/**
 * «Gruba ekle» / «Gruptan çıkar» — which group, over which named scope.
 *
 * The scope is named in words before anything is written, because the two the bar offers are
 * different acts: the records the operator ticked, and every record the filter matches, which is
 * not the page on screen. And what a membership change does NOT do is said here rather than
 * assumed: no certificate is issued or changed and no e-mail is sent.
 */
function GroupMembershipDialog({
  direction,
  scopeName,
  groups,
  chosen,
  onChoose,
  busy,
  onClose,
  onConfirm,
}: {
  direction: 'add' | 'remove';
  scopeName: string;
  groups: CredentialGroupView[];
  chosen: string;
  onChoose: (id: string) => void;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactElement {
  const title = direction === 'add' ? 'Gruba ekle' : 'Gruptan çıkar';
  return (
    <Modal label={title} onClose={onClose}>
      <ModalHead title={title} onClose={onClose} />
      <div className="card-pad stack" style={{ gap: 10 }}>
        <div className="info-box" id="grpDlgScope">
          <b>{scopeName}</b>
          {direction === 'add'
            ? ' seçilen gruba eklenecek. Zaten grupta olan kayıtlar tekrar eklenmez.'
            : ' seçilen gruptan çıkarılacak. Grupta olmayan kayıtlar için bir şey yapılmaz.'}
        </div>
        <label className="field" htmlFor="grpDlgSelect">
          Grup
          <select
            className="select"
            id="grpDlgSelect"
            value={chosen}
            onChange={(e) => onChoose(e.target.value)}
          >
            <option value="">— grup seçin —</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {`${g.name} (${g.memberCount})`}
              </option>
            ))}
          </select>
        </label>
        <div className="help">
          Bu işlem sertifika üretmez, var olan sertifikayı değiştirmez ve e-posta göndermez. Kayıtların hangi
          yüklemeden geldiği de değişmez.
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" type="button" onClick={onClose}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          type="button"
          id="grpDlgGo"
          disabled={busy || chosen === ''}
          onClick={onConfirm}
        >
          {busy ? 'Uygulanıyor…' : title}
        </button>
      </div>
    </Modal>
  );
}
